import { Injectable } from '@angular/core';
import { Platform } from '@ionic/angular';
import { PushNotifications } from '@capacitor/push-notifications';
import { PermissionsService } from './permissions.service';
import { BiometricService } from '../shared/services/capacitor-biometric.class';
import { AnalyticsService } from './analytics.service';

export interface PermissionStatuses {
  location: 'granted' | 'denied' | 'prompt';
  pushNotifications: 'granted' | 'denied' | 'prompt';
  microphone: 'granted' | 'denied' | 'prompt';
  biometric: 'available' | 'unavailable' | 'not_supported';
}

@Injectable({ providedIn: 'root' })
export class PermissionTrackerService {
  constructor(
    private permissionsService: PermissionsService,
    private biometricService: BiometricService,
    private analyticsService: AnalyticsService,
    private platform: Platform
  ) {}

  async checkAndReportPermissions(): Promise<void> {
    try {
      const permissions: PermissionStatuses = {
        location: await this.checkLocation(),
        pushNotifications: await this.checkPushNotifications(),
        microphone: await this.checkMicrophone(),
        biometric: this.checkBiometric()
      };

      this.analyticsService.trackPermissionStatus(permissions as unknown as Record<string, string>);
    } catch (error) {
      console.error('[PermissionTracker] Error checking permissions:', error);
    }
  }

  private async checkLocation(): Promise<'granted' | 'denied' | 'prompt'> {
    try {
      return await this.permissionsService.checkLocationPermission();
    } catch (error) {
      console.error('[PermissionTracker] Error checking location permission:', error);
      return 'prompt';
    }
  }

  private async checkPushNotifications(): Promise<'granted' | 'denied' | 'prompt'> {
    try {
      if (!this.platform.is('capacitor')) {
        return 'prompt';
      }

      const result = await PushNotifications.checkPermissions();
      return result.receive as 'granted' | 'denied' | 'prompt';
    } catch (error) {
      console.error('[PermissionTracker] Error checking push notification permission:', error);
      return 'prompt';
    }
  }

  private async checkMicrophone(): Promise<'granted' | 'denied' | 'prompt'> {
    try {
      return await this.permissionsService.checkMicrophonePermission();
    } catch (error) {
      console.error('[PermissionTracker] Error checking microphone permission:', error);
      return 'prompt';
    }
  }

  private checkBiometric(): 'available' | 'unavailable' | 'not_supported' {
    try {
      const { isAvailable, biometryType } = this.biometricService.biometry;

      if (isAvailable) {
        return 'available';
      } else if (biometryType !== 0) {
        return 'unavailable';
      } else {
        return 'not_supported';
      }
    } catch (error) {
      console.error('[PermissionTracker] Error checking biometric status:', error);
      return 'not_supported';
    }
  }
}
